import { useEffect, useMemo, useRef, useState } from 'react'
import type { LedgerRow, Treatment } from './lib/analysis'
import { ruleMatches, type Rule } from './lib/corrections'
import type { Catalog } from './lib/categories'
import { CategoryPicker, type CategoryChoice } from './CategoryPicker'
import { inSpan, type Span } from './lib/trends'
import { fold, history, textMatches } from './lib/merchants'
import { Bars } from './charts'

const money = new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' })

/** What the drawer lists. Every field narrows; an empty filter lists everything. */
export interface Filter {
  title: string
  /** One category_id. */
  category?: string
  /** A parent and all its subcategories: "Cars". */
  group?: string
  merchant?: string
  /** Text in the merchant or statement description, case and punctuation ignored. */
  query?: string
  month?: string
  account_id?: string
  treatments?: Treatment[]
  /** Only the checking, savings and card activity the spending figures cover: the twelve months, or `span`. */
  windowOnly?: boolean
  /** The time range the dashboard's charts were showing when the list was opened. */
  span?: Span
}

/** Icon + word for how each row is counted; hue carries nothing. */
const TREATMENT: Record<Treatment, { icon: string; word: string }> = {
  spending: { icon: '−', word: 'Spending' },
  income: { icon: '+', word: 'Income' },
  one_off: { icon: '◆', word: 'One-off' },
  excluded: { icon: '⇄', word: 'Not counted' },
}

export type Correction = { category: CategoryChoice; one_off: boolean } | null
export type NewRule = Omit<Rule, 'rule_id' | 'category'> & { category: CategoryChoice }

interface Props {
  filter: Filter
  ledger: LedgerRow[]
  rules: Rule[]
  categories: Catalog
  accountName: (id: string) => string
  /** A save is in flight; controls are disabled until the figures are re-read. */
  busy: boolean
  error?: string
  onCorrect: (row: LedgerRow, change: Correction) => void
  onRule: (rule: NewRule) => void
  onDeleteRule: (rule_id: string) => void
  onAsk: (text: string) => void
  /** Opens every transaction at this merchant, across all history. */
  onMerchant: (merchant: string) => void
  /** Opens another list in the drawer: one subcategory of the group shown. */
  onOpen: (filter: Filter) => void
  onClose: () => void
}

export function matches(f: Filter, r: LedgerRow): boolean {
  return (
    (!f.category || r.category === f.category) &&
    (!f.group || r.group === f.group) &&
    (!f.merchant || r.merchant === f.merchant) &&
    (!f.query || textMatches(f.query, r)) &&
    (!f.month || r.month === f.month) &&
    (!f.account_id || r.account_id === f.account_id) &&
    (!f.treatments || f.treatments.includes(r.treatment)) &&
    (!f.windowOnly || (f.span ? inSpan(r, f.span) : r.in_window))
  )
}

export function TransactionsDrawer({ filter, ledger, rules, categories, accountName, busy, error, onCorrect, onRule, onDeleteRule, onAsk, onMerchant, onOpen, onClose }: Props) {
  const dialog = useRef<HTMLDialogElement>(null)
  const [text, setText] = useState('')
  const [order, setOrder] = useState<'date' | 'amount'>('date')
  const [ruleFor, setRuleFor] = useState<string>()
  // A row corrected out of the filter (spending → one-off or transfer) stays in
  // view with its new treatment, rather than vanishing from under the cursor.
  const [opened] = useState(() => new Set(ledger.filter((r) => matches(filter, r)).map((r) => r.txn_id)))

  useEffect(() => {
    dialog.current?.showModal()
  }, [])

  const rows = useMemo(() => {
    const needle = fold(text)
    return ledger
      .filter((r) => (matches(filter, r) || opened.has(r.txn_id)) && (!needle || fold(`${r.merchant} ${r.description} ${categories.label(r.category)}`).includes(needle)))
      .sort((a, b) => (order === 'date' ? b.date.localeCompare(a.date) : Math.abs(b.amount) - Math.abs(a.amount)))
  }, [ledger, filter, text, order, opened, categories])

  const out = rows.filter((r) => r.amount < 0).reduce((s, r) => s - r.amount, 0)
  const counted = rows.filter((r) => r.treatment === 'spending').reduce((s, r) => s - r.amount, 0)
  const income = rows.filter((r) => r.treatment === 'income').reduce((s, r) => s + r.amount, 0)
  // One merchant or a text search over all history: show how it adds up over time.
  const past = (filter.merchant || filter.query) && !filter.windowOnly ? history(rows) : null
  // A parent category: how its spending splits across its subcategories.
  const subs = filter.group ? split(rows.filter((r) => matches(filter, r))) : []

  return (
    <dialog ref={dialog} className="drawer" onClose={onClose} aria-labelledby="drawer-title">
      <div className="drawer-head">
        <div>
          <p className="label">Transactions</p>
          <h2 id="drawer-title">{filter.title}</h2>
        </div>
        <button className="quiet" onClick={() => dialog.current?.close()} aria-label="Close">✕ Close</button>
      </div>

      <dl className="drawer-stats">
        <div><dt className="label">Rows</dt><dd className="num">{rows.length}</dd></div>
        <div><dt className="label">Counted as spending</dt><dd className="num">{money.format(counted)}</dd></div>
        {out !== counted && <div><dt className="label">All money out</dt><dd className="num">{money.format(out)}</dd></div>}
        {income > 0 && <div><dt className="label">Income</dt><dd className="num">{money.format(income)}</dd></div>}
      </dl>

      {past && past.months.length > 1 && <MerchantHistory h={past} />}
      {subs.length > 1 && (
        <section className="history" aria-label="By subcategory">
          <p className="label">By subcategory</p>
          <Bars
            color="var(--series-2)"
            items={subs}
            total={subs.reduce((s, a) => s + a.total, 0)}
            name={(id) => categories.sub(id)}
            note={(a) => `${a.count} transaction${a.count === 1 ? '' : 's'}`}
            onSelect={(id) => onOpen({ ...filter, group: undefined, category: id, title: subcategoryTitle(filter.title, filter.group!, categories.label(id)) })}
          />
        </section>
      )}

      <div className="drawer-tools">
        <input type="search" placeholder="Search merchant, description or category" value={text} onChange={(e) => setText(e.target.value)} aria-label="Search" />
        <select value={order} onChange={(e) => setOrder(e.target.value as 'date' | 'amount')} aria-label="Order">
          <option value="date">Newest first</option>
          <option value="amount">Largest first</option>
        </select>
        <button
          className="quiet"
          disabled={!rows.length}
          onClick={() => {
            dialog.current?.close()
            onAsk(`About my ${filter.title.toLowerCase()} (${rows.length} transactions, ${money.format(out)} out): `)
          }}
        >
          Ask about these
        </button>
      </div>
      {error && <p className="error">✕ {error}</p>}
      {busy && <p className="muted small">Saving and recounting…</p>}

      <div className="scroll">
        <table className="table txns">
          <thead>
            <tr><th>Date</th><th>Merchant</th><th>Category</th><th>Counted as</th><th className="r">Amount</th></tr>
          </thead>
          <tbody>
            {rows.slice(0, 400).map((r) => (
              <TxnRow
                key={r.txn_id || `${r.date}${r.merchant}${r.amount}`}
                row={r}
                categories={categories}
                account={accountName(r.account_id)}
                busy={busy}
                ruleOpen={ruleFor === r.txn_id}
                ledger={ledger}
                onRuleToggle={() => setRuleFor(ruleFor === r.txn_id ? undefined : r.txn_id)}
                onCorrect={onCorrect}
                onMerchant={filter.merchant === r.merchant ? undefined : () => onMerchant(r.merchant)}
                onRule={(rule) => {
                  setRuleFor(undefined)
                  onRule(rule)
                }}
              />
            ))}
          </tbody>
        </table>
        {rows.length > 400 && <p className="muted small">Showing the first 400 of {rows.length}. Search to narrow the list.</p>}
        {rows.length === 0 && <p className="muted">No transactions match.</p>}
      </div>

      {rules.length > 0 && (
        <>
          <h3 className="subhead">Your rules</h3>
          <ul className="rules">
            {rules.map((r) => (
              <li key={r.rule_id}>
                <span>
                  Anything matching <b>“{r.match}”</b>{r.account_id ? ` on ${accountName(r.account_id)}` : ''} →{' '}
                  {[r.category && categories.label(r.category), r.one_off && '◆ one-off'].filter(Boolean).join(', ') || 'unchanged'}
                </span>
                <button className="link" disabled={busy} onClick={() => onDeleteRule(r.rule_id)}>Delete</button>
              </li>
            ))}
          </ul>
        </>
      )}
      <p className="muted small">
        Changes are saved to the Corrections and Rules tabs of your spreadsheet and the figures recount straight away. Press Re-analyse afterwards to
        update Claude’s review.
      </p>
    </dialog>
  )
}

function TxnRow({
  row,
  categories,
  account,
  busy,
  ruleOpen,
  ledger,
  onRuleToggle,
  onCorrect,
  onMerchant,
  onRule,
}: {
  row: LedgerRow
  categories: Catalog
  account: string
  busy: boolean
  ruleOpen: boolean
  ledger: LedgerRow[]
  onRuleToggle: () => void
  onCorrect: Props['onCorrect']
  onMerchant?: () => void
  onRule: Props['onRule']
}) {
  const t = TREATMENT[row.treatment]
  const canOneOff = row.amount < 0 && (row.treatment === 'spending' || row.treatment === 'one_off')
  return (
    <>
      <tr className={`t-${row.treatment}`}>
        <td className="num">{row.date}</td>
        <td>
          <div>
            {onMerchant ? (
              <button className="link merchant" title={`Every transaction at ${row.merchant}`} onClick={onMerchant}>{row.merchant}</button>
            ) : (
              row.merchant
            )}
          </div>
          <div className="muted small-inline">
            {account}
            {row.description && row.description !== row.merchant && ` · ${row.description}`}
          </div>
          {row.corrected_by && (
            <div className="label">
              ✎ {row.corrected_by === 'you' ? (row.stored_key && !row.txn_id.startsWith('tiller:') ? 'corrected by you on the Tiller row' : 'corrected by you') : row.corrected_by}
              {row.corrected_by === 'you' && (
                <button className="link" disabled={busy} onClick={() => onCorrect(row, null)}>Undo</button>
              )}
            </div>
          )}
        </td>
        <td>
          <CategoryPicker
            label={`Category for ${row.merchant}`}
            value={row.category}
            categories={categories}
            disabled={busy || !row.txn_id}
            onPick={(c) => onCorrect(row, { category: c, one_off: row.treatment === 'one_off' })}
          />
          {row.extracted_category !== row.category && <div className="label">was {categories.label(row.extracted_category)}</div>}
        </td>
        <td>
          <span className="treatment" title={row.reason || undefined}><span aria-hidden>{t.icon}</span> {t.word}</span>
          {row.reason && <div className="muted small-inline">{row.reason}</div>}
          {canOneOff && (
            <label className="check">
              <input
                type="checkbox"
                checked={row.treatment === 'one_off'}
                disabled={busy || !row.txn_id}
                onChange={(e) => onCorrect(row, { category: row.category, one_off: e.target.checked })}
              />{' '}
              One-off
            </label>
          )}
          <button className="link" disabled={busy} onClick={onRuleToggle}>{ruleOpen ? 'Cancel rule' : 'Make a rule…'}</button>
        </td>
        <td className="r num">{money.format(row.amount)}</td>
      </tr>
      {ruleOpen && (
        <tr className="rule-row">
          <td colSpan={5}>
            <RuleForm row={row} categories={categories} account={account} ledger={ledger} busy={busy} onSave={onRule} />
          </td>
        </tr>
      )}
    </>
  )
}

function RuleForm({ row, categories, account, ledger, busy, onSave }: { row: LedgerRow; categories: Catalog; account: string; ledger: LedgerRow[]; busy: boolean; onSave: Props['onRule'] }) {
  const [match, setMatch] = useState(row.merchant)
  const [scope, setScope] = useState<'any' | 'this'>('any')
  const [category, setCategory] = useState<CategoryChoice>(row.category)
  const [oneOff, setOneOff] = useState(row.treatment === 'one_off')
  // While "Add a category…" is open the picker's choice isn't made yet, so saving would use the old one.
  const [adding, setAdding] = useState(false)
  const rule = { match: match.trim(), account_id: scope === 'this' ? row.account_id : '', category, one_off: oneOff }
  const hits = rule.match ? ledger.filter((r) => ruleMatches({ ...rule, category: '', rule_id: '' }, r)).length : 0
  return (
    <form
      className="rule-form"
      onSubmit={(e) => {
        e.preventDefault()
        if (rule.match && !adding) onSave(rule)
      }}
    >
      <span>Treat anything whose merchant or description contains</span>
      <input aria-label="Text to match" value={match} onChange={(e) => setMatch(e.target.value)} />
      <select aria-label="Which accounts" value={scope} onChange={(e) => setScope(e.target.value as 'any' | 'this')}>
        <option value="any">on any account</option>
        <option value="this">on {account} only</option>
      </select>
      <span>as</span>
      <CategoryPicker label="Category" value={category} categories={categories} onPick={setCategory} onAdding={setAdding} />
      <label className="check"><input type="checkbox" checked={oneOff} onChange={(e) => setOneOff(e.target.checked)} /> one-off</label>
      <button type="submit" disabled={busy || !rule.match || adding}>Save rule</button>
      <span className="muted small-inline">{adding ? 'Add the new category first. ' : ''}Matches {hits} transaction{hits === 1 ? '' : 's'} now, and future statements.</span>
    </form>
  )
}

/** Visits, totals and a bar per month from the first transaction to the last. */
function MerchantHistory({ h }: { h: NonNullable<ReturnType<typeof history>> }) {
  const word = h.net < 0 ? 'received' : 'spent'
  const sign = h.net < 0 ? -1 : 1
  const bars = h.months.map((m) => ({ ...m, total: Math.max(0, sign * m.total) }))
  return (
    <section className="history" aria-label="History">
      <dl className="drawer-stats">
        <div><dt className="label">Net {word}</dt><dd className="num">{money.format(Math.abs(h.net))}</dd></div>
        <div><dt className="label">Average each</dt><dd className="num">{money.format(Math.abs(h.average))}</dd></div>
        <div><dt className="label">Per month</dt><dd className="num">{money.format(Math.abs(h.perMonth))}</dd></div>
        <div><dt className="label">First</dt><dd className="num">{h.first}</dd></div>
        <div><dt className="label">Latest</dt><dd className="num">{h.last}</dd></div>
      </dl>
      <Bars
        color="var(--series-2)"
        items={bars.some((m) => m.total > 0) ? bars : []}
        format={(n) => money.format(n)}
        note={(m) => `${m.count} transaction${m.count === 1 ? '' : 's'}`}
        name={(m) => `${m} · ${h.months.find((x) => x.label === m)?.count ?? 0}×`}
      />
    </section>
  )
}

/**
 * "Spending on cars, 2025-11-04 to 2026-11-04" → "Spending on cars / fuel,
 * 2025-11-04 to 2026-11-04": the group's name swapped for the subcategory's,
 * keeping whatever the dashboard added after it.
 */
export function subcategoryTitle(title: string, group: string, label: string): string {
  // After " on ", so a group named "Pending" doesn't match inside "Spending on".
  const on = title.toLowerCase().indexOf(` on ${group.toLowerCase()}`)
  if (on < 0) return `${title}: ${label.toLowerCase()}`
  const at = on + ' on '.length
  return `${title.slice(0, at)}${label.toLowerCase()}${title.slice(at + group.length)}`
}

/** Spending in each category among the rows, largest first, keyed by category_id. */
function split(rows: LedgerRow[]): { label: string; total: number; count: number }[] {
  const by = new Map<string, { label: string; total: number; count: number }>()
  for (const r of rows) {
    if (r.treatment !== 'spending') continue
    const a = by.get(r.category) ?? { label: r.category, total: 0, count: 0 }
    a.total -= r.amount
    a.count += 1
    by.set(r.category, a)
  }
  return [...by.values()].filter((a) => a.total > 0).sort((a, b) => b.total - a.total)
}
